import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, Plane, Download, X, Sparkles, Settings2, ListChecks, Activity,
} from "lucide-react";
import {
    fetchFlightsOverview, fetchAdminFlightBookings, fetchAdminFlightDetail,
    transitionFlightBooking, fetchFlightSettings, saveFlightSettings, fetchFlightServerInfo,
    fetchFlightsHealth, exportFlightsCsv, formatApiError, inr,
} from "@/lib/api";

const STATE_STYLE = {
    FARE_CONFIRMED: "bg-blue-100 text-blue-700", PAYMENT_PENDING: "bg-amber-100 text-amber-700",
    PAYMENT_CONFIRMED: "bg-blue-100 text-blue-700", BOOKING_PENDING: "bg-amber-100 text-amber-700",
    BOOKING_CONFIRMED: "bg-green-100 text-green-700", TICKET_ISSUANCE_PENDING: "bg-amber-100 text-amber-700",
    TICKETED: "bg-green-100 text-green-700", BOOKING_FAILED: "bg-red-100 text-red-600",
    CANCELLATION_REQUESTED: "bg-orange-100 text-orange-700", REFUND_PENDING: "bg-orange-100 text-orange-700",
    REFUNDED: "bg-stone-200 text-stone-600", PARTIALLY_REFUNDED: "bg-stone-200 text-stone-600",
};
const ALL_STATES = Object.keys(STATE_STYLE);

function fmtDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function DetailDrawer({ id, token, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [toState, setToState] = useState("");
    const [pnr, setPnr] = useState("");
    const [tickets, setTickets] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => fetchAdminFlightDetail(token, id).then(setData)
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [token, id]);
    useEffect(() => { load(); }, [load]);

    const b = data?.booking;
    const doTransition = async () => {
        if (!toState) return;
        setBusy(true);
        try {
            await transitionFlightBooking(token, id, {
                to_state: toState, pnr: pnr || null,
                ticket_numbers: tickets ? tickets.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean) : null,
                note,
            });
            toast.success(`Moved to ${toState}`);
            setToState(""); setPnr(""); setTickets(""); setNote("");
            await load(); onChanged();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm flex justify-end" data-testid="flight-detail-drawer">
            <div className="w-full max-w-2xl bg-cream h-full overflow-y-auto shadow-2xl">
                <div className="sticky top-0 bg-navy text-cream px-5 py-4 flex items-center justify-between z-10">
                    <p className="font-display font-black text-lg">{b ? b.reference : "Loading…"}</p>
                    <button onClick={onClose} data-testid="drawer-close" className="text-cream/70 hover:text-cream"><X className="h-5 w-5" /></button>
                </div>
                {!b ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div> : (
                    <div className="p-5 space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_STYLE[b.state]}`} data-testid="drawer-state">{b.state}</span>
                            {b.demo && <span className="rounded-full bg-gold/20 border border-gold/60 px-2.5 py-1 text-[10px] font-black text-navy">DEMO DATA</span>}
                            <span className="text-xs text-stone-500">supplier: {b.supplier}</span>
                        </div>

                        <div className="rounded-xl bg-white border border-stone-200 p-4 text-sm">
                            <p className="font-bold text-navy mb-2">Journey</p>
                            {(b.offers_snapshot || []).map((o, i) => (
                                <div key={i} className="py-1.5 border-b border-stone-100 last:border-0">
                                    <p className="font-semibold text-navy">{o.segments[0].from} → {o.segments[o.segments.length - 1].to} · {o.airline} {o.flight_nos.join("·")}</p>
                                    <p className="text-xs text-stone-500">{fmtDT(o.segments[0].depart)} → {fmtDT(o.segments[o.segments.length - 1].arrive)} · {o.fare_family} · {o.refundable ? "Refundable" : "Non-refundable"}</p>
                                </div>
                            ))}
                        </div>

                        <div className="rounded-xl bg-white border border-stone-200 p-4 text-sm">
                            <p className="font-bold text-navy mb-2">Passengers ({b.passengers.length})</p>
                            {b.passengers.map((p, i) => (
                                <p key={i} className="py-0.5 text-stone-600">{p.first_name} {p.last_name} <span className="text-xs text-stone-400">({p.type}{p.dob ? ` · DOB ${p.dob}` : ""})</span></p>
                            ))}
                            <p className="mt-2 pt-2 border-t border-stone-100 text-stone-600">
                                {b.contact.name} · {b.contact.email} · {b.contact.phone}
                            </p>
                        </div>

                        <div className="rounded-xl bg-white border border-stone-200 p-4 text-sm">
                            <p className="font-bold text-navy mb-2">Money</p>
                            {[["Base fare", b.pricing.base_fare], ["Taxes & fees", b.pricing.taxes + b.pricing.supplier_fee],
                                ["Service fee", b.pricing.service_fee], ["Service charge (markup)", b.pricing.markup]].map(([l, v]) => (
                                <div key={l} className="flex justify-between py-0.5 text-stone-600"><span>{l}</span><span>{inr(v)}</span></div>
                            ))}
                            <div className="flex justify-between pt-1.5 mt-1 border-t border-stone-200 font-black text-navy"><span>Customer total</span><span>{inr(b.pricing.total)}</span></div>
                            <p className="mt-2 text-xs text-stone-500">Payment: <b>{b.payment.status}</b>
                                {b.payment.order_id && <> · order {b.payment.order_id}</>}
                                {b.payment.payment_id && <> · pay {b.payment.payment_id}</>}</p>
                            {b.associate_code && <p className="text-xs text-saffron font-bold mt-1">Referred by partner {b.associate_code}</p>}
                            {b.pnr && <p className="text-xs text-green-700 font-bold mt-1">PNR {b.pnr} · Tickets: {(b.ticket_numbers || []).join(", ")}</p>}
                        </div>

                        <div className="rounded-xl bg-white border border-stone-200 p-4 text-sm">
                            <p className="font-bold text-navy mb-2">History</p>
                            <div className="space-y-1.5">
                                {(b.history || []).map((h, i) => (
                                    <p key={i} className="text-xs text-stone-600 flex gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-saffron mt-1.5 shrink-0" />
                                        <span><b>{h.from || "—"} → {h.to}</b> · {h.by} · {fmtDT(h.at)}{h.note ? ` · ${h.note}` : ""}</span>
                                    </p>
                                ))}
                            </div>
                        </div>

                        {b.allowed_transitions?.length > 0 && (
                            <div className="rounded-xl bg-white border border-stone-200 p-4" data-testid="transition-box">
                                <p className="font-bold text-navy text-sm mb-3">Move booking</p>
                                <select data-testid="transition-select" value={toState} onChange={(e) => setToState(e.target.value)}
                                    className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy">
                                    <option value="">Choose next status…</option>
                                    {b.allowed_transitions.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {toState === "TICKETED" && (
                                    <div className="mt-3 space-y-2">
                                        <input data-testid="transition-pnr" placeholder="Airline PNR (e.g. X7K2PQ)" value={pnr}
                                            onChange={(e) => setPnr(e.target.value.toUpperCase())}
                                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy" />
                                        <textarea data-testid="transition-tickets" placeholder={`Ticket numbers — one per passenger (${b.passengers.length} needed), comma or newline separated`}
                                            value={tickets} onChange={(e) => setTickets(e.target.value)} rows={2}
                                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy" />
                                    </div>
                                )}
                                <input data-testid="transition-note" placeholder="Note (optional — added to audit trail)" value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    className="mt-2 w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy" />
                                <button data-testid="transition-apply" onClick={doTransition} disabled={!toState || busy}
                                    className="mt-3 w-full rounded-full bg-navy text-cream font-display font-black py-2.5 hover:bg-saffron transition-colors disabled:opacity-50">
                                    {busy ? "Updating…" : "Apply status change"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function HealthPanel({ token }) {
    const [data, setData] = useState(null);
    const load = useCallback(() => fetchFlightsHealth(token).then(setData)
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [token]);
    useEffect(() => { load(); }, [load]);
    if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;
    const s = data.supplier;
    const bar = (stats) => stats && stats.total > 0 ? (
        <div className="mt-2 h-2 rounded-full bg-stone-200 overflow-hidden">
            <div className="h-full bg-green-500" style={{ width: `${stats.success_rate}%` }} />
        </div>
    ) : <p className="mt-2 text-xs text-stone-400">No supplier calls in this window</p>;
    return (
        <div className="space-y-4" data-testid="supplier-health">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-stone-200 bg-white p-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Active supplier</p>
                    <p className="font-display font-black text-lg text-navy" data-testid="health-supplier">{s.label}</p>
                    <p className="text-xs mt-1 font-semibold flex items-center gap-1.5">
                        <span className={`h-2.5 w-2.5 rounded-full ${s.configured ? "bg-green-500" : "bg-red-400"}`} />
                        {s.demo ? "Demo inventory" : s.configured ? "Credentials configured" : "Not configured"}
                    </p>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Live booking</p>
                    <p className="font-display font-black text-lg text-navy" data-testid="health-booking">
                        {data.booking_enabled && !s.demo ? "ON" : "OFF"}</p>
                    <p className="text-xs text-stone-500 mt-1">Payments (Razorpay): {data.payments_enabled ? "connected" : "not configured"}</p>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Searches (24h)</p>
                    <p className="font-display font-black text-2xl text-navy" data-testid="health-searches">{data.searches_24h}</p>
                    <p className="text-xs text-stone-500 mt-1">Bookings (24h): {data.bookings_24h}</p>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Supplier calls failed (24h)</p>
                    <p className={`font-display font-black text-2xl ${data.stats_24h.failed ? "text-red-600" : "text-green-700"}`} data-testid="health-failures">{data.stats_24h.failed}</p>
                    <p className="text-xs text-stone-500 mt-1">Checked {fmtDT(data.checked_at)}</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                    <p className="font-display font-black text-navy">Supplier success rate</p>
                    {["stats_24h", "stats_7d"].map((k) => (
                        <div key={k} className="mt-3" data-testid={`health-${k}`}>
                            <div className="flex justify-between text-sm">
                                <span className="font-bold text-navy">{k === "stats_24h" ? "Last 24 hours" : "Last 7 days"}</span>
                                <span className="text-stone-500">{data[k].total ? `${data[k].ok}/${data[k].total} ok (${data[k].success_rate}%)` : "—"}</span>
                            </div>
                            {bar(data[k])}
                        </div>
                    ))}
                    <p className="mt-3 text-[11px] text-stone-400">Capabilities: {Object.entries(s.capabilities).filter(([, v]) => v).map(([k]) => k).join(" · ")}</p>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                    <p className="font-display font-black text-navy mb-2">Recent supplier failures</p>
                    {data.recent_failures.length === 0 ? (
                        <p className="text-sm text-green-700 font-semibold" data-testid="health-no-failures">No failures in the last 7 days.</p>
                    ) : (
                        <div className="space-y-2" data-testid="health-failure-list">
                            {data.recent_failures.map((t) => (
                                <div key={t.id} className="rounded-xl border border-red-100 bg-red-50/50 px-3 py-2 text-xs">
                                    <p className="font-bold text-red-700">{t.action} · {t.supplier} · {fmtDT(t.at)}</p>
                                    <p className="text-stone-500 truncate">{t.error}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="mt-3 text-[11px] text-stone-400">Errors are stored redacted — payloads and credentials are never logged.</p>
                </div>
            </div>
        </div>
    );
}

function SettingsPanel({ token }) {
    const [data, setData] = useState(null);
    const [serverInfo, setServerInfo] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => fetchFlightSettings(token).then((d) => {
        setData(d);
        setForm({ supplier: d.settings.supplier, booking_enabled: d.settings.booking_enabled,
            service_fee_flat: d.settings.service_fee_flat, markup_pct: d.settings.markup_pct });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [token]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        fetchFlightServerInfo(token).then(setServerInfo).catch(() => {});
    }, [token]);

    const save = async () => {
        setBusy(true);
        try {
            await saveFlightSettings(token, { ...form, service_fee_flat: Number(form.service_fee_flat) || 0, markup_pct: Number(form.markup_pct) || 0 });
            toast.success("Flight settings saved");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;
    return (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="flight-settings">
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <p className="font-display font-black text-navy mb-3">Supplier</p>
                <div className="space-y-2">
                    {data.suppliers.map((s) => (
                        <label key={s.key} data-testid={`supplier-${s.key}`}
                            className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${form.supplier === s.key ? "border-saffron bg-cream/60" : "border-stone-200"}`}>
                            <input type="radio" name="supplier" checked={form.supplier === s.key} onChange={() => setForm({ ...form, supplier: s.key })} />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-navy">{s.label}</p>
                                <p className="text-[11px] text-stone-400">
                                    {s.configured ? "Credentials configured" : `Awaiting: ${s.env_required.join(", ") || "—"}`}
                                    {s.configured && s.test_mode === true && <span className="ml-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 font-black text-[9px] uppercase">Sandbox</span>}
                                    {s.configured && s.test_mode === false && <span className="ml-1 rounded-full bg-green-100 text-green-700 px-1.5 py-0.5 font-black text-[9px] uppercase">Production</span>}
                                </p>
                            </div>
                            <span className={`h-2.5 w-2.5 rounded-full ${s.configured ? "bg-green-500" : "bg-stone-300"}`} />
                        </label>
                    ))}
                </div>
                {serverInfo?.outbound_ipv4 && (
                    <div className="mt-3 rounded-xl border border-saffron/40 bg-cream/60 p-3" data-testid="server-ip-info">
                        <p className="text-[11px] font-bold text-navy">This server's IP for the TripJack whitelist:</p>
                        <p className="font-mono text-sm font-black text-saffron select-all" data-testid="server-ip-value">{serverInfo.outbound_ipv4}</p>
                        <p className="mt-1 text-[10px] text-stone-500">TripJack portal → Manage User → API Configuration → Whitelisted IPs (IPv4 only). Each environment (preview / live) has its own IP — whitelist the one shown here on the site you're fixing.</p>
                    </div>
                )}
                <p className="mt-3 text-[11px] text-stone-400">Supplier credentials live in server-side Secrets only — never in the browser. Add them after the commercial agreement (Stage B).</p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <p className="font-display font-black text-navy mb-3">Pricing & booking</p>
                <label className="flex items-center justify-between rounded-xl border border-stone-200 p-3 mb-3">
                    <span className="text-sm font-bold text-navy">Live booking + online payment</span>
                    <input type="checkbox" data-testid="booking-enabled-toggle" checked={!!form.booking_enabled}
                        onChange={(e) => setForm({ ...form, booking_enabled: e.target.checked })} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Service fee (₹ flat)</label>
                        <input type="number" min="0" max="5000" data-testid="service-fee-input" value={form.service_fee_flat}
                            onChange={(e) => setForm({ ...form, service_fee_flat: e.target.value })}
                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">Markup (% of fare)</label>
                        <input type="number" min="0" max="10" step="0.5" data-testid="markup-input" value={form.markup_pct}
                            onChange={(e) => setForm({ ...form, markup_pct: e.target.value })}
                            className="w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm font-semibold text-navy" />
                    </div>
                </div>
                <p className="mt-2 text-[11px] text-stone-400">Both are shown to the customer in the fare summary. Payments: Razorpay {data.payments_enabled ? "connected" : "not configured"}.</p>
                <button data-testid="save-flight-settings" onClick={save} disabled={busy}
                    className="mt-4 w-full rounded-full bg-navy text-cream font-display font-black py-2.5 hover:bg-saffron transition-colors disabled:opacity-50">
                    {busy ? "Saving…" : "Save settings"}
                </button>
            </div>
        </div>
    );
}

export function FlightsManager({ token }) {
    const [tab, setTab] = useState("bookings");
    const [overview, setOverview] = useState(null);
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ q: "", state: "", demo: "" });
    const [loading, setLoading] = useState(true);
    const [detailId, setDetailId] = useState(null);

    const load = useCallback(async (f = filters) => {
        setLoading(true);
        try {
            const [ov, list] = await Promise.all([
                fetchFlightsOverview(token),
                fetchAdminFlightBookings(token, { q: f.q || undefined, state: f.state || undefined, demo: f.demo || undefined }),
            ]);
            setOverview(ov); setRows(list.bookings);
        } catch (e) {
            if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail));
        } finally { setLoading(false); }
    }, [token, filters]);
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div data-testid="flights-manager">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-display font-black text-2xl text-navy flex items-center gap-2"><Plane className="h-6 w-6 text-saffron" />Flight Bookings</h2>
                <div className="flex gap-2">
                    <div className="flex rounded-full border border-stone-300 overflow-hidden text-xs font-bold">
                        {[["bookings", "Bookings", ListChecks], ["health", "Health", Activity], ["settings", "Settings", Settings2]].map(([k, l, Icon]) => (
                            <button key={k} data-testid={`flights-tab-${k}`} onClick={() => setTab(k)}
                                className={`px-4 py-2 flex items-center gap-1.5 ${tab === k ? "bg-navy text-cream" : "text-stone-500"}`}>
                                <Icon className="h-3.5 w-3.5" />{l}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => load()} data-testid="flights-refresh" className="rounded-full border border-stone-300 p-2 text-navy hover:border-saffron"><RefreshCw className="h-4 w-4" /></button>
                    <button onClick={() => exportFlightsCsv(token)} data-testid="flights-export" className="rounded-full border border-stone-300 p-2 text-navy hover:border-saffron"><Download className="h-4 w-4" /></button>
                </div>
            </div>

            {overview?.demo_mode && (
                <div className="mb-4 rounded-xl border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-navy flex gap-2 items-start" data-testid="admin-demo-banner">
                    <Sparkles className="h-4 w-4 mt-0.5 text-saffron shrink-0" />
                    <span><b>Demo supplier active.</b> All flight data is sample inventory. Live booking turns on from Settings once a certified supplier's credentials are configured.</span>
                </div>
            )}

            {tab === "settings" ? <SettingsPanel token={token} /> : tab === "health" ? <HealthPanel token={token} /> : (
                <>
                    {overview && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                            <div className="rounded-2xl border border-stone-200 bg-white p-4">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Total bookings</p>
                                <p className="font-display font-black text-2xl text-navy" data-testid="stat-total">{overview.total}</p>
                            </div>
                            <div className="rounded-2xl border border-stone-200 bg-white p-4">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Paid bookings</p>
                                <p className="font-display font-black text-2xl text-navy" data-testid="stat-paid">{overview.paid_count}</p>
                            </div>
                            <div className="rounded-2xl border border-stone-200 bg-white p-4">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400">Collected revenue</p>
                                <p className="font-display font-black text-2xl text-navy" data-testid="stat-revenue">{inr(overview.revenue)}</p>
                            </div>
                            <div className="rounded-2xl border border-stone-200 bg-white p-4">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400 flex items-center gap-1"><Activity className="h-3 w-3" />Supplier errors</p>
                                <p className="font-display font-black text-2xl text-navy" data-testid="stat-errors">{overview.recent_errors.length}</p>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                        <input data-testid="flights-filter-q" placeholder="Search ref / name / email / phone / PNR" value={filters.q}
                            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            className="rounded-xl border border-stone-300 bg-white px-3.5 py-2 text-sm font-semibold text-navy w-72" />
                        <select data-testid="flights-filter-state" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                            className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                            <option value="">All statuses</option>
                            {ALL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select data-testid="flights-filter-demo" value={filters.demo} onChange={(e) => setFilters({ ...filters, demo: e.target.value })}
                            className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                            <option value="">Demo + live</option><option value="false">Live only</option><option value="true">Demo only</option>
                        </select>
                        <button onClick={() => load()} data-testid="flights-filter-apply"
                            className="rounded-xl bg-navy text-cream px-4 py-2 text-sm font-bold hover:bg-saffron transition-colors">Apply</button>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                    ) : rows.length === 0 ? (
                        <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-500" data-testid="flights-empty">
                            No flight bookings yet. They appear here the moment a customer creates one.
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-stone-200 bg-white overflow-x-auto">
                            <table className="w-full text-sm min-w-[760px]" data-testid="flights-table">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-stone-200">
                                        <th className="px-4 py-3">Reference</th><th className="px-4 py-3">Customer</th>
                                        <th className="px-4 py-3">Route</th><th className="px-4 py-3">Travel</th>
                                        <th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((b) => (
                                        <tr key={b.id} className="border-b border-stone-100 hover:bg-cream/50 cursor-pointer"
                                            data-testid={`flight-row-${b.reference}`} onClick={() => setDetailId(b.id)}>
                                            <td className="px-4 py-3 font-bold text-navy">
                                                {b.reference}
                                                {b.demo && <span className="ml-1.5 rounded-full bg-gold/20 px-1.5 py-0.5 text-[9px] font-black text-navy align-middle">DEMO</span>}
                                            </td>
                                            <td className="px-4 py-3">{b.contact?.name}<br /><span className="text-xs text-stone-400">{b.contact?.phone}</span></td>
                                            <td className="px-4 py-3 font-semibold">{(b.legs || []).map((l) => `${l.origin}→${l.destination}`).join(" · ")}</td>
                                            <td className="px-4 py-3 text-xs text-stone-500">{b.legs?.[0]?.date}</td>
                                            <td className="px-4 py-3 font-bold text-navy">{inr(b.pricing?.total)}</td>
                                            <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATE_STYLE[b.state]}`}>{b.state}</span></td>
                                            <td className="px-4 py-3 text-saffron text-xs font-bold">Open</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {detailId && (
                <DetailDrawer id={detailId} token={token} onClose={() => setDetailId(null)}
                    onChanged={() => load()} />
            )}
        </div>
    );
}
